import React from 'react';
import { ListItemText, Grid, Card, CardHeader, CardContent, CardActions, List, ListItem, Button, Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Menu, MenuItem, IconButton } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { useRevolusendApi } from '../../context';
import utils from '../../services/utils.service';
import moment from 'moment';
import { LoadingButton } from '../../components/loadingButton';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Attachments } from '../../components/attachments';
import { DirectFundingOrder } from '../../services/types/revolusend/directFundingOrder.type';
import DetailsIcon from '@material-ui/icons/Info';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardActions: {
      justifyContent: 'flex-end'
    },
    link: {
      color: 'grey',
      display: 'flex',
      alignItems: 'center',
    },
    icon: {
      marginRight: theme.spacing(0.9),
      fontSize: '1.1rem',
    },
  })
);

const MarkAsPaidDialog = (props: { orderId?: number, open: boolean, onClose: () => void }) => {
  const { open, onClose, orderId } = props;
  const api = useRevolusendApi();
  const { t } = useTranslation('directFundingOrders');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setLoading(false);
  }, [open]);

  const markAsPaid = () => {
    if (!orderId) {
      return;
    }
    utils.runAsync(async () => {
      setLoading(true);
      try {
        await api.changeDirectFundingStatus(orderId, 'COMPLETED');
        onClose();
      } catch (e) {
      }
    }, (e) => {
      setLoading(false);
    })
  }

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>{t('MARK_AS_PAID_MODAL_TITLE')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('MARK_AS_PAID_MODAL_DESCRIPTION')}
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('CANCEL')}
        </Button>
        <LoadingButton color='primary' loading={loading} disabled={loading} onClick={markAsPaid}>
          {t('MARK_AS_PAID_BUTTON_TITLE')}
        </LoadingButton>
      </DialogActions>
    </Dialog >
  )
}

export const DirectFundingOrderDetails = (props: RouteComponentProps<{ id: string }>) => {
  const orderId = props.match.params.id;
  const { t } = useTranslation(['directFundingOrders']);
  const classes = useStyles();
  const api = useRevolusendApi();
  const history = useHistory();

  const [orderDetails, setOrderDetails] = React.useState<DirectFundingOrder | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [isFileSaved, setIsFileSaved] = React.useState(false);

  const [showMarkAsPaidModal, setShowMarkAsPaidModal] = React.useState(false);
  
  const [actionsAnchorEl, setActionsAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };
  const handleActionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const loadTransactionDetails = () => {
    utils.runAsync(async () => {
      setLoading(true);
      setOrderDetails(await api.getDirectFundingOrderDetails(orderId));
    }, (e) => {
      setLoading(false);
      if (e) {
        console.log(e);
      }
    });
  };

  React.useEffect(() => {
    loadTransactionDetails();
  }, []);

  const handleFileSaved = () => {
    if (orderDetails?.status !== 'EXCEPTION_REFUNDED') {
      setIsFileSaved(true);
    }
  };

  React.useEffect(() => {
    if (isFileSaved) {
      setIsFileSaved(false);
    }
  }, [isFileSaved]);

  if (loading && orderDetails === null) {
    return (
      <Grid container justify='center'>
        <Box marginTop={4}>
          <CircularProgress />
        </Box>
      </Grid>
    );
  }

  const getOrderStatusLabel = () => {
    if (orderDetails === null) return '';
    return orderDetails.status;
  };

  return (
    <>

      <MarkAsPaidDialog
        orderId={orderDetails?.id}
        open={showMarkAsPaidModal}
        onClose={() => {
          setShowMarkAsPaidModal(false);
          loadTransactionDetails();
        }}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card>
                <CardHeader title={t('ORDER')} />
                <CardContent>
                  <Grid container>
                    <Grid item xs={12} md={6}>
                      <List dense disablePadding>
                        <ListItem disableGutters>
                          <ListItemText primary={t('ID')} secondary={orderDetails?.id} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('CONSUMER_EXTERNAL_REFERENCE')} secondary={orderDetails?.external_reference} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('CREATED')} secondary={orderDetails?.created ? moment(orderDetails.created).format('DD-MM-YYYY HH:mm:ss') : '-'} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('MODIFIED')} secondary={orderDetails?.updated ? moment(orderDetails.updated).format('DD-MM-YYYY HH:mm:ss') : '-'} />
                        </ListItem>
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <List dense disablePadding>
                        <ListItem disableGutters>
                          <ListItemText primary={t('AMOUNT')} secondary={`${utils.currencyFormat(orderDetails?.amount)}`} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('STATUS')} secondary={t(getOrderStatusLabel())} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText primary={t('PAYMENT_RECEIVED')} secondary={orderDetails?.paid ? t('YES') : t('NO')} />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemText 
                            primary={t('TRANSACTION_ID')} 
                            secondary={
                              orderDetails?.transaction_id ? (
                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={`/revolusend/transfers/${orderDetails.transaction_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ marginRight: 4, padding: 0 }}
                                  >
                                    <DetailsIcon />
                                  </IconButton>
                                  {orderDetails.transaction_id}
                                </span>
                              ) : '-'
                            }
                          />
                        </ListItem>
                      </List>
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions className={classes.cardActions}>
                  <Button variant='contained' color='primary' aria-controls='actions-menu' aria-haspopup='true' onClick={handleActionsClick}>
                    {t('ACTIONS')}
                  </Button>
                  <Menu id='actions-menu' anchorEl={actionsAnchorEl} keepMounted open={Boolean(actionsAnchorEl)} onClose={handleActionsClose}>
                    <MenuItem
                      disabled={orderDetails?.paid}
                      onClick={() => {
                        setShowMarkAsPaidModal(true);
                        handleActionsClose();
                      }}
                    >
                      {t('MARK_AS_PAYMENT_RECEIVED')}
                    </MenuItem>
                  </Menu>
                </CardActions>
              </Card>
            </Grid>
            {orderDetails && (
              <Grid item xs={12}>
                <Attachments
                  entity="directFundingOrder"
                  id={orderDetails.id.toString()}
                  onFileSaved={handleFileSaved}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title={t('ISSUER_DETAILS')} />
            <CardContent>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText primary={t('ISSUER_NAME')} secondary={orderDetails?.issuer_name} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary={t('ISSUER_BANK_ACCOUNT')} secondary={orderDetails?.bank_account} />
                </ListItem>
              </List>
            </CardContent>
            <CardActions className={classes.cardActions}>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};